import { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import AdminUserEditModal from 'components/form/AdminUserEditModal'
import LoadingSpinnerGreen from 'components/ui/LoadingSpinnerGreen'
import { Button, Chip, Grid, MenuItem, Select, TextField, Typography, Box } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import InputAdornment from '@mui/material/InputAdornment'
import { CreateUserIcon } from 'assets/icons/create-user-icon'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import { CircleCheckIcon } from 'assets/icons/circle-check-icon'
import { CircleXIcon } from 'assets/icons/circle-x-icon'
import IconButton from '@mui/material/IconButton'
import AdminUserEditPopover from 'components/form/AdminUserEditPopover'
import AdminUserCreateModal from 'components/form/AdminUserCreateModal'
import {
  createCognitoUser,
  deleteCognitoUser,
  editCognitoUser,
  getCognitoUsers,
  resendCognitoInvitation,
} from 'store/cognitoUser/thunks'
import { COGNITO_GPS_EDIT_ADMIN, COGNITO_USER } from 'config'
import { useAppDispatch } from 'store'
import { selectCognitoUsers } from 'store/cognitoUser/slice'
import useGetAdminGroup from 'hooks/useGetAdminGroup'
import { useAuth } from 'react-oidc-context'

const ROLE_TO_COGNITO_GROUP = {
  courseEditor: COGNITO_USER,
  adminCourseEditor: COGNITO_GPS_EDIT_ADMIN,
}

export default function Administrators(): JSX.Element {
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const { users, status, nextToken } = useSelector(selectCognitoUsers)
  const [selectedRoleType, setSelectedRoleType] = useState<string>('administrator')
  const [adminUserModalOpen, setAdminUserModalOpen] = useState<boolean>(false)
  const [createAdminUserModalOpen, setCreateAdminUserModalOpen] = useState<boolean>(false)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [selectedUser, setSelectedUser] = useState<CognitoUser | null>(null)
  const [adminAction, setAdminAction] = useState<AdminUserAction | null>(null)
  const [searchText, setSearchText] = useState<string>('')
  const cognitoAdministrator = useGetAdminGroup()
  const authUser = useAuth()

  const openAdminUserMenu = (event: React.MouseEvent<HTMLElement>, user: CognitoUser) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
    setSelectedUser(user)
  }

  const closeAdminUserMenu = () => {
    setAnchorEl(null)
  }

  const closeAdminUserEditModal = () => {
    setAdminUserModalOpen(!adminUserModalOpen)
    setAdminAction(null)
  }

  useEffect(() => {
    if (selectedRoleType === 'administrator') {
      dispatch(getCognitoUsers({ group: cognitoAdministrator, nextToken: nextToken }))
      return
    }
    dispatch(getCognitoUsers({ group: ROLE_TO_COGNITO_GROUP[selectedRoleType], nextToken: nextToken }))
  }, [selectedRoleType])

  const openAdminUserEditModal = (user: CognitoUser) => {
    setAdminAction('edit')
    setAdminUserModalOpen(!adminUserModalOpen)
    setSelectedUser(user)
  }

  const openAdminCreateUserModal = () => {
    setAdminAction('create')
    setCreateAdminUserModalOpen(!createAdminUserModalOpen)
  }

  const closeAdminCreateUserModal = () => {
    setCreateAdminUserModalOpen(!createAdminUserModalOpen)
    setAdminAction(null)
  }

  const openAdminUserDeleteModal = (user: CognitoUser) => {
    setAdminAction('delete')
    setAdminUserModalOpen(!adminUserModalOpen)
    setSelectedUser(user)
  }

  const openAdminUserResendModal = (user: CognitoUser) => {
    setAdminAction('resendInvitation')
    setAdminUserModalOpen(!adminUserModalOpen)
    setSelectedUser(user)
  }

  const rows = useMemo(() => {
    if (!users) {
      return []
    }
    return users
  }, [users])

  const filteredRows = useMemo(() => {
    let filtered = rows

    if (searchText.trim()) {
      const searchLower = searchText.toLowerCase()
      filtered = filtered.filter(
        (row) =>
          (row.email && row.email.toLowerCase().includes(searchLower)) ||
          String(row.ggbUserId).toLowerCase().includes(searchLower),
      )
    }

    return filtered
  }, [rows, searchText, selectedRoleType])

  //cognito group is determines which role the user has.
  const handleCreateUser = (data: CognitoUserCreateForm) => {
    const payload = {
      cognitoGroup: ROLE_TO_COGNITO_GROUP[data.role],
      email: data.email,
      phoneNumber: data.phone,
      ggbUserId: data.userId,
    }
    dispatch(createCognitoUser(payload))
    setCreateAdminUserModalOpen(!createAdminUserModalOpen)
    setAdminAction(null)
  }

  const handleEditUser = (data: CognitoUserEditForm) => {
    if (!selectedUser) {
      return
    }
    const payload: CognitoUserEditPayload = {
      username: selectedUser.username,
      email: data.email,
      phoneNumber: data.phone,
      internalUserId: data.userId?.toString(),
      successMessage: intl.formatMessage({ id: 'strings.cognitoUserEditSuccessMessage' }),
    }

    dispatch(editCognitoUser(payload))
    setAdminUserModalOpen(!adminUserModalOpen)
    setAdminAction(null)
  }

  const handleDeleteUser = (payload: string) => {
    dispatch(
      deleteCognitoUser({
        username: payload,
        successMessage: intl.formatMessage({ id: 'strings.userDeletedSuccessfully' }),
      }),
    )
    setAdminUserModalOpen(!adminUserModalOpen)
    setAdminAction(null)
  }

  const handleResendInvitation = () => {
    if (!selectedUser) {
      return
    }
    dispatch(
      resendCognitoInvitation({
        email: selectedUser.email,
        successMessage: intl.formatMessage({ id: 'strings.userCognitoResendSuccessMessage' }),
      }),
    )
    setAdminUserModalOpen(!adminUserModalOpen)
  }

  const columns: GridColDef<CognitoUser>[] = [
    {
      field: 'email',
      headerName: intl.formatMessage({ id: 'strings.email' }),
      flex: 1.5,
      renderCell: ({ row }) => {
        if (row.email === authUser.user?.profile.email) {
          return (
            <>
              {row.email}{' '}
              <Chip
                label="You"
                sx={{
                  color: '#373D40',
                  lineHeight: '16px',
                  bgcolor: '#F5F5F5',
                  fontSize: '14px',
                  fontWeight: 400,
                  letterSpacing: '0.15px',
                  height: '24px',
                  padding: '2px 8px 2px 6px',
                  '& .MuiChip-label': {
                    px: 0.5,
                  },
                  gap: '4px',
                  textAlign: 'center',
                }}
              />
            </>
          )
        }
        if (row.UserStatus === 'FORCE_CHANGE_PASSWORD') {
          return (
            <>
              {row.email}{' '}
              <Chip
                label="Invite pending"
                sx={{
                  color: '#373D40',
                  lineHeight: '16px',
                  bgcolor: '#F5F5F5',
                  fontSize: '14px',
                  fontWeight: 400,
                  letterSpacing: '0.15px',
                  height: '24px',
                  padding: '2px 8px 2px 6px',
                  '& .MuiChip-label': {
                    px: 0.5,
                  },
                  gap: '4px',
                  textAlign: 'center',
                }}
              />
            </>
          )
        }
        return row.email
      },
    },
    {
      field: 'ggbUserId',
      headerName: 'User ID',
      flex: 1,
      renderCell: ({ row }) => {
        return (
          row.ggbUserId ?? (
            <span
              style={{
                color: '#8A8A8E',
                fontSize: '20px',
                fontStyle: 'normal',
                fontWeight: '400',
                lineHeight: '20px',
                letterSpacing: '0',
              }}
            >
              -
            </span>
          )
        )
      },
    },
    {
      field: 'UserStatus',
      headerName: 'Status',
      sortable: false,
      flex: 1,
      renderCell: ({ row }) => {
        return row.UserStatus === 'CONFIRMED' || row.UserStatus === 'EXTERNAL_PROVIDER' ? (
          <Chip
            icon={<CircleCheckIcon />}
            label="2FA"
            sx={{
              color: '#15803D',
              bgcolor: '#F0FDF4',
              fontSize: '14px',
              fontWeight: 400,
              letterSpacing: '0.15px',
              height: '24px',
              padding: '2px 8px 2px 6px',
              '& .MuiChip-label': {
                px: 0.5,
              },
              gap: '4px',
            }}
          />
        ) : (
          <Chip
            icon={<CircleXIcon />}
            label="2FA"
            sx={{
              color: '#373D40',
              lineHeight: '16px',
              bgcolor: '#F5F5F5',
              fontSize: '14px',
              fontWeight: 400,
              letterSpacing: '0.15px',
              height: '24px',
              padding: '2px 8px 2px 6px',
              '& .MuiChip-label': {
                px: 0.5,
              },
              gap: '4px',
              textAlign: 'center',
            }}
          />
        )
      },
    },
    {
      field: '',
      sortable: false,
      flex: 0.5,
      renderCell: ({ row }) => (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <IconButton
            size="small"
            onClick={(e) => {
              openAdminUserMenu(e, row)
            }}
            sx={{
              color: '#0BB163',
              width: 32,
              height: 32,
              '&:hover': {
                backgroundColor: '#EEEEF0',
                borderRadius: '10px',
              },
              ml: 'auto',
            }}
          >
            <MoreHorizIcon sx={{ width: '32px', height: '32px' }} />
          </IconButton>
        </Box>
      ),
    },
  ]

  return (
    <>
      <Grid container gap={4} padding={3} direction={'column'}>
        <Grid item>
          <Typography sx={{ fontSize: '24px', fontWeight: '700', display: 'flex', lineHeight: '28px' }}>
            Administrators
          </Typography>
        </Grid>
        <Grid container direction={'row'}>
          <Grid item sx={{ display: 'flex', gap: '12px', width: '100%' }}>
            <TextField
              placeholder="Search by email or internal user id"
              sx={{
                display: 'flex',
                gap: '8px',

                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  height: '40px',
                  width: '280px',
                },
              }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Select
              value={selectedRoleType}
              onChange={(e) => setSelectedRoleType(e.target.value)}
              sx={{
                display: 'flex',
                width: '170px',
                height: '40px',
                borderRadius: '10px',
              }}
            >
              <MenuItem value="administrator">{intl.formatMessage({ id: 'strings.administrator' })}</MenuItem>
              <MenuItem value="adminCourseEditor">{intl.formatMessage({ id: 'strings.courseEditorAdmin' })}</MenuItem>
              <MenuItem value="courseEditor">{intl.formatMessage({ id: 'strings.courseEditor' })}</MenuItem>
            </Select>
            <Button
              sx={{
                gap: '2px',
                border: 'solid 1px',
                display: 'flex',
                ml: 'auto',
                height: '41px',
                borderRadius: '10px',
                background: '#0BB163',
                color: '#F7FEE7',
                lineHeight: '20px',
                fontSize: '14px',
                fontWeight: '500',
                textTransform: 'none',
                padding: '10px 18px',
                alignItems: 'center',
                '&:hover': {
                  background: '#098E4F',
                },
              }}
              startIcon={<CreateUserIcon />}
              onClick={openAdminCreateUserModal}
            >
              Create user
            </Button>
          </Grid>
        </Grid>
        <Grid item>
          {status === 'loading' ? (
            <SpinnerContainer>
              <LoadingSpinnerGreen size={72} />
            </SpinnerContainer>
          ) : (
            <DataGrid
              rows={filteredRows}
              columns={columns}
              getRowId={(row) => row.username}
              disableColumnMenu
              disableRowSelectionOnClick
              autoHeight
              sx={{
                // eslint-disable-next-line max-len
                '& div.MuiDataGrid-cell:focus, div.MuiDataGrid-cell:focus-within, div.MuiDataGrid-columnHeader:focus-within':
                  {
                    outline: 'none',
                  },
                '& .MuiDataGrid-columnHeaders': {
                  height: 42,
                  minHeight: 42,
                  maxHeight: 42,
                  backgroundColor: '#098E4F',
                  borderRadius: '10px 10px 0 0',
                  overflow: 'hidden',
                  borderTopLeftRadius: '9px',
                  borderTopRightRadius: '9px',
                },
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: '#098E4F',
                  color: '#fff',
                  fontWeight: 'bold',
                  minHeight: 42,
                  maxHeight: 42,
                  height: 42,
                  display: 'flex',
                  alignItems: 'center',
                  lineHeight: '51px',
                },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                },
                '& .MuiDataGrid-sortIcon': {
                  color: '#FFFFFF',
                },
                '& .MuiDataGrid-menuIcon': {
                  color: '#FFFFFF',
                },
                '& .MuiDataGrid-iconSeparator': {
                  color: '#FFFFFF',
                },
                borderRadius: '10px',
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'transparent',
                },
              }}
              initialState={{
                pagination: { paginationModel: { pageSize: 50 } },
              }}
              rowHeight={54}
            />
          )}
        </Grid>
        <AdminUserEditPopover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={closeAdminUserMenu}
          user={selectedUser}
          onEdit={openAdminUserEditModal}
          onResendInvitation={openAdminUserResendModal}
          onDelete={openAdminUserDeleteModal}
        />

        {adminAction && (
          <AdminUserEditModal
            open={adminUserModalOpen}
            user={selectedUser}
            action={adminAction}
            onClose={closeAdminUserEditModal}
            onSubmit={handleEditUser}
            onDelete={handleDeleteUser}
            onResend={handleResendInvitation}
          />
        )}

        {adminAction === 'create' && (
          <AdminUserCreateModal
            open={createAdminUserModalOpen}
            onClose={closeAdminCreateUserModal}
            onSubmit={handleCreateUser}
          />
        )}
      </Grid>
    </>
  )
}

const SpinnerContainer = styled.div`
  display: flex;
  width: 100%;
  margin: 50px auto;
  justify-content: center;
`
